import { useEffect, useState, useCallback } from "react";

/**
 * Reads / writes the persistent handle for an active Full-Scan.
 *
 * The handle survives modal close, page navigation and browser refresh via
 * ``sessionStorage["cibraxis:activeFullScan"]``.  We propagate mutations to
 * every listener in the same tab through a custom event because the native
 * ``storage`` event only fires cross-tab — the tab that wrote the change
 * would otherwise never see it.
 */
const KEY = "cibraxis:activeFullScan";
const EVT = "cibraxis:activeFullScanChange";

function _read() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setActiveFullScan(v) {
  if (v) sessionStorage.setItem(KEY, JSON.stringify(v));
  else sessionStorage.removeItem(KEY);
  // Notify same-tab listeners (storage event fires only in OTHER tabs).
  window.dispatchEvent(new CustomEvent(EVT));
}

export function useActiveFullScan() {
  const [state, setState] = useState(_read);

  useEffect(() => {
    const refresh = () => setState(_read());
    window.addEventListener(EVT, refresh);
    window.addEventListener("storage", refresh);
    // Also refresh whenever the page regains focus — the scan may have
    // completed while another tab was in the foreground.
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener(EVT, refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const set = useCallback((v) => setActiveFullScan(v), []);
  return [state, set];
}
